"use client";

import { Menu } from "@base-ui/react/menu";
import { IconChevronDownMedium } from "central-icons/IconChevronDownMedium";
import { useReducedMotion } from "motion/react";
import { useState } from "react";
import Comparison from "@/components/preview/comparison";
import DemoSwitch from "@/components/preview/demo-switch";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const ITEM_CLASS =
  "flex cursor-default select-none items-center rounded-md px-2.5 py-1.5 text-gray-1200 text-sm outline-none data-highlighted:bg-gray-300";

const POPUP =
  "transition-[translate,scale,opacity,filter] duration-200 ease-out will-change-transform data-starting-style:-translate-y-1 data-starting-style:scale-95 data-starting-style:opacity-0 data-starting-style:blur-xs data-ending-style:-translate-y-1 data-ending-style:scale-95 data-ending-style:opacity-0 data-ending-style:blur-xs";

const POPUP_REDUCED =
  "transition-opacity duration-200 ease-out data-starting-style:opacity-0 data-ending-style:opacity-0";

const CHEVRON = "transition-[rotate,color] duration-200 ease-out";

const CHEVRON_REDUCED = "transition-colors duration-200 ease-out";

function OptionsMenu({ reduced }: { reduced?: boolean }) {
  return (
    <Menu.Root>
      <Menu.Trigger
        render={
          <Button
            className="group"
            rightIcon={
              <IconChevronDownMedium
                className={cn(
                  "size-4.5 text-gray-1000 hover-hover:group-hover:text-gray-1200 group-data-popup-open:-rotate-180",
                  reduced ? CHEVRON_REDUCED : CHEVRON
                )}
              />
            }
            rounded
            size="md"
            static
            variant="secondary"
          />
        }
      >
        Options
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner className="outline-none" sideOffset={8}>
          <Menu.Popup
            className={cn(
              "z-50 min-w-44 origin-(--transform-origin) rounded-lg bg-preview-bg p-1 text-gray-1200 shadow-custom outline-none",
              reduced ? POPUP_REDUCED : POPUP
            )}
          >
            <Menu.Item className={ITEM_CLASS}>New folder</Menu.Item>
            <Menu.Item className={ITEM_CLASS}>Get info</Menu.Item>
            <Menu.Separator className="-mx-1 my-1 h-px bg-gray-300" />
            <Menu.Item className={ITEM_CLASS}>Sort by</Menu.Item>
            <Menu.Item className={ITEM_CLASS}>Show view options</Menu.Item>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

export default function AccessibilityReducedMotionDemo() {
  const prefersReduced = useReducedMotion();
  const [override, setOverride] = useState<boolean | null>(null);
  const reduced = override ?? prefersReduced ?? false;

  return (
    <Comparison
      bad={<OptionsMenu />}
      footer={
        <DemoSwitch
          checked={reduced}
          label="Reduced motion"
          onCheckedChange={setOverride}
        />
      }
      good={<OptionsMenu reduced={reduced} />}
      panelClassName="h-64 justify-start pt-8"
      stackBelow="md"
    />
  );
}
